import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, TrendingDown, Eye, MousePointer, Globe, Package } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

export default function StoreAnalytics() {
  const navigate = useNavigate();
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('7');
  
  useEffect(() => {
    fetchData();
  }, [storeId, days]);
  
  useEffect(() => {
    if (store) {
      document.title = `${store.name} Analytics - Fluencer`;
    }
  }, [store]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [storeRes, analyticsRes] = await Promise.all([
        fetch(`${API}/stores/${storeId}`, { credentials: 'include' }),
        fetch(`${API}/stores/${storeId}/analytics?days=${days}`, { credentials: 'include' }),
      ]);
      
      if (!storeRes.ok) throw new Error('Store not found');
      
      const storeData = await storeRes.json();
      setStore(storeData);
      
      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setAnalytics(analyticsData);
      }
    } catch (error) {
      toast.error('Failed to load analytics');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  const chartData = analytics?.daily_stats?.map(d => ({
    ...d,
    date: formatDate(d.date)
  })) || [];
  
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-products-button"
              variant="ghost"
              size="sm"
              className="rounded-full"
              onClick={() => navigate(`/store/${storeId}/products`)}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-outfit font-bold">Analytics</h1>
              <p className="text-muted-foreground">{store?.name}</p>
            </div>
          </div>
          
          {/* Time Range Selector */}
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger data-testid="time-range-select" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Page Views Card */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Eye className="w-5 h-5" />
                <span className="text-sm font-medium">Page Views</span>
              </div>
              {analytics?.views_change !== 0 && (
                <div className={`flex items-center gap-1 text-sm ${analytics?.views_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analytics?.views_change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  <span>{Math.abs(analytics?.views_change || 0)}%</span>
                </div>
              )}
            </div>
            <p className="text-4xl font-outfit font-bold">{analytics?.total_views?.toLocaleString() || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">vs previous {days} days</p>
          </div>
          
          {/* Affiliate Clicks Card */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MousePointer className="w-5 h-5" />
                <span className="text-sm font-medium">Affiliate Clicks</span>
              </div>
              {analytics?.clicks_change !== 0 && (
                <div className={`flex items-center gap-1 text-sm ${analytics?.clicks_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analytics?.clicks_change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  <span>{Math.abs(analytics?.clicks_change || 0)}%</span>
                </div>
              )}
            </div>
            <p className="text-4xl font-outfit font-bold">{analytics?.total_clicks?.toLocaleString() || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">vs previous {days} days</p>
          </div>
        </div>
        
        {/* Chart */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <h2 className="text-xl font-outfit font-bold mb-6">Traffic Over Time</h2>
          {chartData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={false}
                    name="Page Views"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="clicks" 
                    stroke="#f97316" 
                    strokeWidth={2}
                    dot={false}
                    name="Affiliate Clicks"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-80 flex items-center justify-center text-muted-foreground">
              <p>No data available for this period</p>
            </div>
          )}
        </div>
        
        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Domain Breakdown */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <Globe className="w-5 h-5 text-muted-foreground" />
              <h2 className="text-xl font-outfit font-bold">Traffic by Domain</h2>
            </div>
            {analytics?.domains?.length > 0 ? (
              <div className="space-y-4">
                {analytics.domains.map((domain, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium truncate max-w-[200px]">{domain.domain}</span>
                        <span className="text-sm text-muted-foreground">{domain.views} views</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${domain.percentage}%` }}
                        />
                      </div>
                    </div>
                    <span className="ml-4 text-sm font-medium w-12 text-right">{domain.percentage}%</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No domain data yet</p>
            )}
          </div>
          
          {/* Top Products */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <Package className="w-5 h-5 text-muted-foreground" />
              <h2 className="text-xl font-outfit font-bold">Top Products</h2>
            </div>
            {analytics?.top_products?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_products.slice(0, 5).map((product, idx) => (
                  <div key={product.product_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                    <span className="text-sm font-bold text-muted-foreground w-6">{idx + 1}</span>
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.title}
                        className="w-10 h-10 object-cover rounded"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
                        <Package className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{product.title}</p>
                      <div className="flex gap-4 text-xs text-muted-foreground">
                        <span>{product.views} views</span>
                        <span>{product.clicks} clicks</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No product data yet</p>
            )}
          </div>
        </div>
        
        {/* Empty State Message */}
        {analytics?.total_views === 0 && analytics?.total_clicks === 0 && (
          <div className="mt-8 bg-muted/30 border border-border rounded-xl p-8 text-center">
            <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-outfit font-bold mb-2">No Analytics Data Yet</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Analytics will start populating once visitors view your published store. 
              Make sure your store is published and share the link!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
